import { useState } from 'react'
import { Play, Pause, DollarSign } from 'lucide-react'
import type { BrowsableVoice } from './types'

const CATEGORY_STYLES: Record<string, { bg: string; text: string }> = {
  premade: { bg: 'bg-zinc-200 dark:bg-zinc-700', text: 'text-zinc-600 dark:text-zinc-300' },
  professional: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-400' },
  cloned: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-700 dark:text-yellow-400' },
  generated: { bg: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-700 dark:text-purple-400' },
  local: { bg: 'bg-zinc-200 dark:bg-zinc-700', text: 'text-zinc-600 dark:text-zinc-300' },
}

const MAX_LANGUAGE_BADGES = 4

const MODEL_DISPLAY_NAMES: Record<string, string> = {
  eleven_multilingual_v2: 'Multilingual v2',
  eleven_multilingual_v1: 'Multilingual v1',
  eleven_monolingual_v1: 'English v1',
  eleven_turbo_v2: 'Turbo v2',
  eleven_turbo_v2_5: 'Turbo v2.5',
  eleven_flash_v2: 'Flash v2',
  eleven_flash_v2_5: 'Flash v2.5',
}

/** Check if a voice has extra costs beyond the normal ElevenLabs subscription. */
function hasExtraCost(voice: BrowsableVoice): boolean {
  const meta = voice.provider_metadata
  if (!meta) return false
  const rate = meta.rate as number | undefined
  const freeAllowed = meta.free_users_allowed as boolean | undefined
  // rate > 1.0 means premium pricing; free_users_allowed=false means paid-only
  return (rate != null && rate > 1) || freeAllowed === false
}

interface VoiceCardProps {
  voice: BrowsableVoice
  isPlaying: boolean
  onPlay: () => void
  onSelect: () => void
}

export function VoiceCard({ voice, isPlaying, onPlay, onSelect }: VoiceCardProps) {
  const [showLangTooltip, setShowLangTooltip] = useState(false)
  const categoryStyle = CATEGORY_STYLES[voice.category ?? ''] ?? CATEGORY_STYLES.premade
  const visibleLanguages = voice.languages.slice(0, MAX_LANGUAGE_BADGES)
  const overflowCount = voice.languages.length - MAX_LANGUAGE_BADGES
  const extraCost = hasExtraCost(voice)
  const supportedModels = (voice.provider_metadata?.supported_models as string[] | undefined) ?? []
  const modelNames = supportedModels.map((m) => MODEL_DISPLAY_NAMES[m] ?? m)
  const [showDescTooltip, setShowDescTooltip] = useState(false)
  const metaParts = [voice.gender, voice.age, voice.accent]
    .filter(Boolean)
    .map((s) => s!.charAt(0).toUpperCase() + s!.slice(1))
  const descTruncated = voice.description && voice.description.length > 60
  const descDisplay = voice.description
    ? descTruncated
      ? voice.description.slice(0, 57) + '...'
      : voice.description
    : null

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
        isPlaying
          ? 'bg-primary/10 border-primary'
          : 'bg-card border-border hover:border-muted-foreground/40'
      }`}
    >
      {/* Play / Pause button */}
      <button
        onClick={onPlay}
        disabled={!voice.preview_url}
        className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${
          isPlaying
            ? 'bg-primary text-primary-foreground'
            : voice.preview_url
              ? 'bg-muted text-foreground hover:bg-muted-foreground/10'
              : 'bg-muted/60 text-muted-foreground cursor-not-allowed'
        }`}
        title={voice.preview_url ? (isPlaying ? 'Pause' : 'Play preview') : 'No preview available'}
      >
        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>

      {/* Voice info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-foreground text-sm">{voice.name}</span>
          {voice.category && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${categoryStyle.bg} ${categoryStyle.text} uppercase`}>
              {voice.category}
            </span>
          )}
          {extraCost && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-warning/10 text-warning-strong font-medium flex items-center gap-0.5"
              title="This voice may incur additional costs beyond your ElevenLabs subscription"
            >
              <DollarSign className="w-2.5 h-2.5" />
              PAID
            </span>
          )}
          {visibleLanguages.map((lang) => (
            <span
              key={lang}
              className="text-[10px] px-1.5 py-0.5 rounded-sm bg-info/10 text-info font-medium uppercase"
            >
              {lang}
            </span>
          ))}
          {overflowCount > 0 && (
            <span
              className="text-[10px] text-info cursor-help relative"
              onMouseEnter={() => setShowLangTooltip(true)}
              onMouseLeave={() => setShowLangTooltip(false)}
            >
              +{overflowCount}
              {showLangTooltip && (
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-popover border border-border rounded-sm text-[10px] text-popover-foreground whitespace-nowrap shadow-lg z-10">
                  {voice.languages.map((l) => l.toUpperCase()).join(', ')}
                </span>
              )}
            </span>
          )}
        </div>
        {(metaParts.length > 0 || descDisplay) && (
          <div className="text-xs text-muted-foreground mt-1 truncate">
            {metaParts.join(' · ')}
            {metaParts.length > 0 && descDisplay && ' · '}
            {descDisplay && (
              <span
                className={descTruncated ? 'cursor-help relative' : ''}
                onMouseEnter={() => descTruncated && setShowDescTooltip(true)}
                onMouseLeave={() => setShowDescTooltip(false)}
              >
                {descDisplay}
                {showDescTooltip && voice.description && (
                  <span className="absolute bottom-full left-0 mb-1 px-2 py-1.5 bg-popover border border-border rounded-sm text-[11px] text-popover-foreground whitespace-normal max-w-[350px] shadow-lg z-10 leading-relaxed">
                    {voice.description}
                  </span>
                )}
              </span>
            )}
          </div>
        )}
        {modelNames.length > 0 && (
          <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
            Models: {modelNames.join(', ')}
          </div>
        )}
      </div>

      {/* Select button */}
      <button
        onClick={onSelect}
        className={`shrink-0 text-xs px-3 py-1.5 rounded-md transition-colors ${
          isPlaying
            ? 'bg-primary text-primary-foreground'
            : 'border border-primary text-primary hover:bg-primary/10'
        }`}
      >
        Select
      </button>
    </div>
  )
}
